import { useContext, useEffect, useState, useMemo } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import AuthContext from 'units/auth/AuthContext'
import Loader from 'components/Loader'
import FetchContext from 'context/fetch/FetchContext'
import PlatformContext from 'context/platform/PlatformContext'
import AnnouncementsContext from 'context/announcements/AnnouncementsContext'
import { SessionStartupOrchestrator } from 'units/session/SessionStartupOrchestrator'
import { ScannerImageUploadTask } from 'units/session/tasks/ScannerImageUploadTask'
import { AnnouncementSessionStartTask } from 'units/session/tasks/AnnouncementSessionStartTask'
import useRenderTrace from 'units/debug/useRenderTrace'

const AuthWrapper = () => {
    const { user, token, authenticated } = useContext(AuthContext)
    const fetcher = useContext(FetchContext)
    const platform = useContext(PlatformContext)
    const announcements = useContext(AnnouncementsContext)

    const orchestrator = useMemo(() => new SessionStartupOrchestrator([
        new ScannerImageUploadTask(platform.scannedImagesLocation),
        new AnnouncementSessionStartTask(announcements.receiveSessionStart),
    ]), [announcements.receiveSessionStart])

    const location = useLocation()
    const [from, setFrom] = useState(null)
    const [previousUserId, setPreviousUserId] = useState(null)

    const queryClient = useQueryClient()

    useRenderTrace('AuthWrapper', {
        user,
        token,
        authenticated,
        fetcher,
        platform,
        announcements,
        orchestrator,
        location,
        from,
        previousUserId,
    })

    useEffect(() => {
        if (authenticated && user && fetcher && platform) {
            orchestrator.run({ user, fetcher, platform })
        }
    }, [authenticated, user, fetcher, platform, orchestrator])

    useEffect(() => {
        if (user && user.id !== previousUserId) {
            setPreviousUserId(user.id)
        }

        let target = '/'
        if (previousUserId == null) {
            target = location
        }
        else if (user && user.id === previousUserId) {
            target = location
        }
        setFrom(target)
    }, [previousUserId])

    let content
    if (authenticated) {
        content = <Outlet />
    }
    else if (from == null) {
        content = <Loader minHeight="50vh" />
    }
    else {
        content = <Navigate to="/auth/sign-in" replace state={{ from }} />
    }

    return content
}

export default AuthWrapper
